
import React, { useState, useMemo } from "react";
import useFinancialTransactions from "../../hooks/useFinancialTransactions";
import { TRANSACTION_TYPES } from "../../constants/transactionTypes";
import styles from "./OverviewTab.module.css";

const MONTHS = [
  "يناير",
  "فبراير",
  "مارس",
  "أبريل",
  "مايو",
  "يونيو",
  "يوليو",
  "أغسطس",
  "سبتمبر",
  "أكتوبر",
  "نوفمبر",
  "ديسمبر",
];

const OverviewTab = () => {
  const {
    transactions,
    loading,
    error,
    filters,
    setFilters,
  } = useFinancialTransactions();
  const [hasApplied, setHasApplied] = useState(false);
  // =====================================================
  // Date Filter
  // =====================================================

  const [showDateFilter, setShowDateFilter] =
    useState(false);

  /*
   * day | month | year
   */
  const [filterType, setFilterType] =
    useState("day");

  /*
   * From | To
   */
  const [selectingField, setSelectingField] =
    useState("From");

  /*
   * التاريخ المؤكد فعليًا
   */
  const [fromDate, setFromDate] =
    useState(null);

  const [toDate, setToDate] =
    useState(null);

  /*
   * التاريخ المؤقت داخل الـ Modal
   *
   * لا يتم اعتماده إلا عند الضغط على "تأكيد"
   */
  const [tempDate, setTempDate] =
    useState(null);

  /*
   * التاريخ المعروض داخل الـ Modal
   */
  const [selectedYear, setSelectedYear] =
    useState(new Date().getFullYear());

  const [selectedMonth, setSelectedMonth] =
    useState(new Date().getMonth());

  const [selectedDay, setSelectedDay] =
    useState(new Date().getDate());

  // =====================================================
  // Helpers
  // =====================================================

  const pad = (value) =>
    String(value).padStart(2, "0");

  const formatDisplayDate = (date) => {
    if (!date) return "";

    return `${pad(date.getDate())}/${pad(
      date.getMonth() + 1
    )}/${date.getFullYear()}`;
  };

  const formatApiStart = (date) => {
    return `${date.getFullYear()}-${pad(
      date.getMonth() + 1
    )}-${pad(date.getDate())}T00:00:00`;
  };

  const formatApiEnd = (date) => {
    return `${date.getFullYear()}-${pad(
      date.getMonth() + 1
    )}-${pad(date.getDate())}T23:59:59`;
  };

  // =====================================================
  // Open Date Modal
  // =====================================================

  const openDateFilter = (field) => {
    setSelectingField(field);

    /*
     * لو فيه تاريخ مؤكد بالفعل،
     * نفتح عليه.
     *
     * لو مفيش، نبدأ من تاريخ اليوم.
     */
    const currentDate =
      field === "From"
        ? fromDate
        : toDate;

    const dateToUse =
      currentDate || new Date();

    /*
     * مهم جدًا:
     * بنعمل نسخة من التاريخ.
     */
    const copiedDate = new Date(
      dateToUse
    );

    setTempDate(copiedDate);

    setSelectedYear(
      copiedDate.getFullYear()
    );

    setSelectedMonth(
      copiedDate.getMonth()
    );

    setSelectedDay(
      copiedDate.getDate()
    );

    setShowDateFilter(true);
  };

  // =====================================================
  // Temporary Date
  // =====================================================

  const selectTempDate = (date) => {
    if (!date) return;

    setTempDate(date);

    setSelectedYear(
      date.getFullYear()
    );

    setSelectedMonth(
      date.getMonth()
    );

    setSelectedDay(
      date.getDate()
    );
  };

  // =====================================================
  // Select Day
  // =====================================================

  const handleSelectDay = (day) => {
    const date = new Date(
      selectedYear,
      selectedMonth,
      day
    );

    selectTempDate(date);
  };

  // =====================================================
  // Select Month
  // =====================================================

  const handleSelectMonth = (monthIndex) => {
    let date;

    /*
     * From الشهر يبدأ من أول يوم
     */
    if (selectingField === "From") {
      date = new Date(
        selectedYear,
        monthIndex,
        1
      );
    } else {
      /*
       * To الشهر ينتهي بآخر يوم
       */
      date = new Date(
        selectedYear,
        monthIndex + 1,
        0
      );
    }

    selectTempDate(date);
  };

  // =====================================================
  // Select Year
  // =====================================================

  const handleSelectYear = (year) => {
    let date;

    /*
     * From السنة = أول يوم في السنة
     */
    if (selectingField === "From") {
      date = new Date(
        year,
        0,
        1
      );
    } else {
      /*
       * To السنة = آخر يوم في السنة
       */
      date = new Date(
        year,
        11,
        31
      );
    }

    selectTempDate(date);
  };

  // =====================================================
  // Confirm Date
  // =====================================================

  const confirmDateSelection = () => {
    if (!tempDate) return;

    /*
     * هنا فقط يتم حفظ التاريخ الحقيقي.
     */
    if (selectingField === "From") {
      setFromDate(
        new Date(tempDate)
      );
    } else {
      setToDate(
        new Date(tempDate)
      );
    }

    /*
     * هنا فقط نقفل الـ Modal.
     */
    setShowDateFilter(false);
  };

  // =====================================================
  // Cancel Date
  // =====================================================

  const cancelDateSelection = () => {
    /*
     * لا نلمس fromDate أو toDate.
     */
    setShowDateFilter(false);
  };

  // =====================================================
  // Apply Filter
  // =====================================================

  const applyDateFilter = () => {
    if (!fromDate || !toDate) {
      alert(
        "من فضلك اختر التاريخ من وإلى"
      );

      return;
    }

    if (fromDate > toDate) {
      alert(
        "تاريخ البداية يجب أن يكون قبل تاريخ النهاية"
      );

      return;
    }

    /*
     * هنا فقط يتغير filters
     *
     * وبالتالي useFinancialTransactions
     * سيعمل GET جديد.
     */
    setFilters((prev) => ({
      ...prev,

      From: formatApiStart(
        fromDate
      ),

      To: formatApiEnd(
        toDate
      ),

      PageIndex: 1,
    }));
    setHasApplied(true);
  };

  // =====================================================
  // Clear Filter
  // =====================================================

  const clearDateFilter = () => {
    setFromDate(null);
    setToDate(null);
    setTempDate(null);

    setFilters((prev) => ({
      ...prev,

      From: "",
      To: "",

      PageIndex: 1,
    }));
    setHasApplied(false);
  };

  // =====================================================
  // Calculate Overview
  // =====================================================

  const {
    totalExpense,
    totalRevenue,
    net,
  } = useMemo(() => {
    const totalExpense =
      transactions
        .filter(
          (t) =>
            Number(t.transactionType) ===
            Number(
              TRANSACTION_TYPES.EXPENSE
            )
        )
        .reduce(
          (sum, t) =>
            sum + Number(t.amount || 0),
          0
        );

    const totalRevenue =
      transactions
        .filter(
          (t) =>
            Number(t.transactionType) ===
            Number(
              TRANSACTION_TYPES.REVENUE
            )
        )
        .reduce(
          (sum, t) =>
            sum + Number(t.amount || 0),
          0
        );

    return {
      totalExpense,
      totalRevenue,
      net:
        totalRevenue -
        totalExpense,
    };
  }, [transactions]);

  // =====================================================
  // Last Transactions
  // =====================================================

  const recentTransactions =
    useMemo(() => {
      return [...transactions]
        .sort(
          (a, b) =>
            new Date(
              b.date ||
                b.createdAt ||
                b.transactionDate ||
                0
            ) -
            new Date(
              a.date ||
                a.createdAt ||
                a.transactionDate ||
                0
            )
        )
        .slice(0, 5);
    }, [transactions]);

  // =====================================================
  // Calendar
  // =====================================================

  const daysInMonth =
    new Date(
      selectedYear,
      selectedMonth + 1,
      0
    ).getDate();

  const currentYear =
    new Date().getFullYear();

  /*
   * آخر 5 سنوات
   */
  const years = Array.from(
    { length: 5 },
    (_, index) =>
      currentYear - index
  );

  // =====================================================
  // Render
  // =====================================================

  return (
    <div
      className={styles.container}
      dir="rtl"
    >
      {/* =================================================
          HEADER
      ================================================== */}

      <div className={styles.header}>
        <div>
          <h3>نظرة عامة</h3>

          <p className={styles.subtitle}>
            ملخص الإيرادات والمصروفات
          </p>
        </div>
      </div>

      {/* =================================================
          DATE FILTER
      ================================================== */}

      <div className={styles.filterBox}>

        {/* FROM */}

        <div className={styles.dateField}>
          <label>من</label>

          <button
            type="button"
            className={styles.dateButton}
            onClick={() =>
              openDateFilter("From")
            }
          >
            {fromDate
              ? formatDisplayDate(
                  fromDate
                )
              : "اختر تاريخ البداية"}
          </button>
        </div>

        {/* TO */}

        <div className={styles.dateField}>
          <label>إلى</label>

          <button
            type="button"
            className={styles.dateButton}
            onClick={() =>
              openDateFilter("To")
            }
          >
            {toDate
              ? formatDisplayDate(
                  toDate
                )
              : "اختر تاريخ النهاية"}
          </button>
        </div>

        {/* ACTIONS */}

        <div className={styles.filterActions}>

          <button
            type="button"
            className={styles.applyButton}
            onClick={applyDateFilter}
          >
            تطبيق
          </button>

          <button
            type="button"
            className={styles.clearButton}
            onClick={clearDateFilter}
          >
            مسح
          </button>

        </div>
      </div>

      {/* =================================================
          SELECTED RANGE
      ================================================== */}

      <div
        className={
          styles.selectedRange
        }
      >
        <span>
          الفترة المختارة:
        </span>

        <strong>
          {fromDate
            ? formatDisplayDate(
                fromDate
              )
            : "غير محدد"}

          <span
            className={styles.arrow}
          >
            {" → "}
          </span>

          {toDate
            ? formatDisplayDate(
                toDate
              )
            : "غير محدد"}
        </strong>
      </div>

      {/* =================================================
          ERROR
      ================================================== */}

      {error && (
        <p className={styles.error}>
          {error}
        </p>
      )}

      {/* =================================================
          LOADING
      ================================================== */}

    {!hasApplied ? (
  <p className={styles.empty}>
    اختر الفترة (من - إلى) ثم اضغط "تطبيق" لعرض الملخص
  </p>
) : loading ? (
  <p className={styles.loading}>جاري التحميل...</p>
) : (
  <>
    {/* =============================================
        CARDS
    ============================================== */}

    <div className={styles.cards}>

      {/* Revenue */}
      <div className={`${styles.card} ${styles.revenue}`}>
        <span>إجمالي الإيرادات</span>
        <h2>{totalRevenue.toLocaleString("ar-EG")} ج.م</h2>
      </div>

      {/* Expense */}
      <div className={`${styles.card} ${styles.expense}`}>
        <span>إجمالي المصروفات</span>
        <h2>{totalExpense.toLocaleString("ar-EG")} ج.م</h2>
      </div>

      {/* Net */}
      <div
        className={`${styles.card} ${
          net >= 0 ? styles.positive : styles.negative
        }`}
      >
        <span>الصافي</span>
        <h2>{net.toLocaleString("ar-EG")} ج.م</h2>
      </div>

    </div>

    {/* =============================================
        RECENT TRANSACTIONS
    ============================================== */}

    <h3 className={styles.recentTitle}>آخر العمليات</h3>

    {recentTransactions.length === 0 ? (
      <p className={styles.empty}>لا توجد عمليات مالية</p>
    ) : (
      <ul className={styles.recentList}>
        {recentTransactions.map((transaction) => {
          const isExpense =
            Number(transaction.transactionType) ===
            Number(TRANSACTION_TYPES.EXPENSE);

          return (
            <li key={transaction.id}>
              <span>{transaction.title}</span>
              <span className={isExpense ? styles.neg : styles.pos}>
                {isExpense ? "-" : "+"}
                {Number(transaction.amount || 0).toLocaleString("ar-EG")} ج.م
              </span>
            </li>
          );
        })}
      </ul>
    )}
  </>
)}
      {/* =================================================
          DATE MODAL
      ================================================== */}

      {showDateFilter && (
        <div
          className={
            styles.modalOverlay
          }
          onClick={
            cancelDateSelection
          }
        >
          <div
            className={styles.modal}
            onClick={(event) =>
              event.stopPropagation()
            }
          >

            {/* ===========================================
                MODAL HEADER
            ============================================ */}

            <div
              className={
                styles.modalHeader
              }
            >
              <div>
                <h3>
                  اختيار{" "}
                  {selectingField ===
                  "From"
                    ? "تاريخ البداية"
                    : "تاريخ النهاية"}
                </h3>

                <p>
                  اختر اليوم أو الشهر أو
                  السنة ثم اضغط تأكيد
                </p>
              </div>

              <button
                type="button"
                className={
                  styles.closeButton
                }
                onClick={
                  cancelDateSelection
                }
              >
                ×
              </button>
            </div>

            {/* ===========================================
                TYPE TABS
            ============================================ */}

            <div
              className={
                styles.typeTabs
              }
            >
              <button
                type="button"
                className={
                  filterType === "day"
                    ? styles.activeTab
                    : ""
                }
                onClick={() =>
                  setFilterType("day")
                }
              >
                يوم
              </button>

              <button
                type="button"
                className={
                  filterType === "month"
                    ? styles.activeTab
                    : ""
                }
                onClick={() =>
                  setFilterType("month")
                }
              >
                شهر
              </button>

              <button
                type="button"
                className={
                  filterType === "year"
                    ? styles.activeTab
                    : ""
                }
                onClick={() =>
                  setFilterType("year")
                }
              >
                سنة
              </button>
            </div>

            {/* ===========================================
                DAY
            ============================================ */}

            {filterType === "day" && (
              <>
                <div
                  className={
                    styles.calendarHeader
                  }
                >
                  <button
                    type="button"
                    onClick={() => {
                      const previous =
                        new Date(
                          selectedYear,
                          selectedMonth -
                            1,
                          1
                        );

                      setSelectedYear(
                        previous.getFullYear()
                      );

                      setSelectedMonth(
                        previous.getMonth()
                      );
                    }}
                  >
                    ‹
                  </button>

                  <strong>
                    {
                      MONTHS[
                        selectedMonth
                      ]
                    }{" "}
                    {selectedYear}
                  </strong>

                  <button
                    type="button"
                    onClick={() => {
                      const next =
                        new Date(
                          selectedYear,
                          selectedMonth +
                            1,
                          1
                        );

                      setSelectedYear(
                        next.getFullYear()
                      );

                      setSelectedMonth(
                        next.getMonth()
                      );
                    }}
                  >
                    ›
                  </button>
                </div>

                <div
                  className={
                    styles.dayGrid
                  }
                >
                  {Array.from(
                    {
                      length:
                        daysInMonth,
                    },
                    (_, index) => {
                      const day =
                        index + 1;

                      const isSelected =
                        tempDate &&
                        tempDate.getFullYear() ===
                          selectedYear &&
                        tempDate.getMonth() ===
                          selectedMonth &&
                        tempDate.getDate() ===
                          day;

                      return (
                        <button
                          key={day}
                          type="button"
                          className={
                            isSelected
                              ? `${styles.dayButton} ${styles.selected}`
                              : styles.dayButton
                          }
                          onClick={() =>
                            handleSelectDay(
                              day
                            )
                          }
                        >
                          {day}
                        </button>
                      );
                    }
                  )}
                </div>
              </>
            )}

            {/* ===========================================
                MONTH
            ============================================ */}

            {filterType === "month" && (
              <>
                <div
                  className={
                    styles.calendarHeader
                  }
                >
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedYear(
                        (prev) =>
                          prev - 1
                      )
                    }
                  >
                    ‹
                  </button>

                  <strong>
                    {selectedYear}
                  </strong>

                  <button
                    type="button"
                    onClick={() =>
                      setSelectedYear(
                        (prev) =>
                          prev + 1
                      )
                    }
                  >
                    ›
                  </button>
                </div>

                <div
                  className={
                    styles.monthGrid
                  }
                >
                  {MONTHS.map(
                    (
                      month,
                      index
                    ) => {
                      const isSelected =
                        tempDate &&
                        tempDate.getFullYear() ===
                          selectedYear &&
                        tempDate.getMonth() ===
                          index;

                      return (
                        <button
                          key={month}
                          type="button"
                          className={
                            isSelected
                              ? `${styles.monthButton} ${styles.selected}`
                              : styles.monthButton
                          }
                          onClick={() =>
                            handleSelectMonth(
                              index
                            )
                          }
                        >
                          {month}
                        </button>
                      );
                    }
                  )}
                </div>
              </>
            )}

            {/* ===========================================
                YEAR
            ============================================ */}

            {filterType === "year" && (
              <div
                className={
                  styles.yearGrid
                }
              >
                {years.map(
                  (year) => {
                    const isSelected =
                      tempDate &&
                      tempDate.getFullYear() ===
                        year;

                    return (
                      <button
                        key={year}
                        type="button"
                        className={
                          isSelected
                            ? `${styles.yearButton} ${styles.selected}`
                            : styles.yearButton
                        }
                        onClick={() =>
                          handleSelectYear(
                            year
                          )
                        }
                      >
                        {year}
                      </button>
                    );
                  }
                )}
              </div>
            )}

            {/* ===========================================
                MODAL FOOTER
            ============================================ */}

            <div
              className={
                styles.modalFooter
              }
            >
              <div
                className={
                  styles.tempSelectedDate
                }
              >
                <span>
                  الاختيار الحالي:
                </span>

                <strong>
                  {tempDate
                    ? formatDisplayDate(
                        tempDate
                      )
                    : "لم يتم الاختيار"}
                </strong>
              </div>

              <div
                className={
                  styles.modalActions
                }
              >
                <button
                  type="button"
                  className={
                    styles.cancelModalButton
                  }
                  onClick={
                    cancelDateSelection
                  }
                >
                  إلغاء
                </button>

                <button
                  type="button"
                  className={
                    styles.confirmModalButton
                  }
                  disabled={!tempDate}
                  onClick={
                    confirmDateSelection
                  }
                >
                  تأكيد
                </button>
              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default OverviewTab;

